import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { usd, pct } from "@/lib/format";
import { toast } from "sonner";

function moneyNeg(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const v = Math.abs(Number(n));
  return `−${usd(v)}`;
}

function Stepper({ onUp, onDown, upLabel, downLabel }) {
  return (
    <span className="ez-pc-step">
      <button type="button" onClick={onUp} aria-label={upLabel}>
        <i className="ez-pc-chev up" aria-hidden />
      </button>
      <button type="button" onClick={onDown} aria-label={downLabel}>
        <i className="ez-pc-chev down" aria-hidden />
      </button>
    </span>
  );
}

function MoneyField({
  label,
  value,
  onChange,
  hint,
  testid,
  step = 0.01,
  min = 0,
  readOnly = false,
  onReadOnlyClick,
  prefix = "$",
}) {
  const bump = (dir) => {
    if (readOnly) return;
    const cur = value === "" || value == null || !Number.isFinite(Number(value)) ? 0 : Number(value);
    const next = Math.max(min, +(cur + dir * step).toFixed(step < 1 ? 2 : 0));
    onChange(next);
  };
  return (
    <label className="ez-pc-field">
      <span className="ez-pc-label">{label}</span>
      <span className={cn("ez-pc-shell", readOnly && "read")}>
        {prefix ? <span className="ez-pc-dollar">{prefix}</span> : null}
        {readOnly ? (
          <button
            type="button"
            className="ez-pc-value"
            data-testid={testid}
            title={onReadOnlyClick ? "Use as cost price" : undefined}
            onClick={onReadOnlyClick}
          >
            {value == null || value === "" ? "—" : Number(value).toFixed(2)}
          </button>
        ) : (
          <>
            <input
              data-testid={testid}
              type="number"
              min={min}
              step={step}
              value={value === null || value === undefined ? "" : value}
              aria-label={label}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") onChange(null);
                else onChange(parseFloat(raw));
              }}
            />
            <Stepper
              onUp={() => bump(1)}
              onDown={() => bump(-1)}
              upLabel={`Increase ${label}`}
              downLabel={`Decrease ${label}`}
            />
          </>
        )}
      </span>
      {hint ? <span className="ez-pc-help">{hint}</span> : null}
    </label>
  );
}

function QtyField({ value, onChange, hint, testid }) {
  const bump = (dir) => {
    const cur = Math.max(1, Math.round(Number(value) || 1));
    onChange(Math.max(1, cur + dir));
  };
  return (
    <label className="ez-pc-field">
      <span className="ez-pc-label">Quantity</span>
      <span className="ez-pc-shell">
        <input
          data-testid={testid}
          type="number"
          min={1}
          step={1}
          value={value ?? 1}
          aria-label="Quantity"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") onChange(1);
            else onChange(Math.max(1, Math.round(parseFloat(raw)) || 1));
          }}
        />
        <Stepper
          onUp={() => bump(1)}
          onDown={() => bump(-1)}
          upLabel="Increase quantity"
          downLabel="Decrease quantity"
        />
      </span>
      {hint ? <span className="ez-pc-help">{hint}</span> : null}
    </label>
  );
}

/**
 * Product Analyzer profit calculator — prototype visual language,
 * real computeCalc math, scenario-only fulfillment toggle.
 */
export function ProfitCalculator({
  input,
  calc,
  product,
  fulfillment,
  onFulfillmentChange,
  setField,
  onReset,
  advanced,
  setAdvanced,
  hideZero,
  setHideZero,
}) {
  const [breakdownOpen, setBreakdownOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const netTone =
    calc.net_profit == null ? "neutral"
      : calc.net_profit < 0 ? "danger"
        : (calc.roi_percent != null && calc.roi_percent >= calc.target_roi) ? "success"
          : "warning";
  const roiText = calc.roi_percent == null ? "—" : pct(calc.roi_percent);
  const marginText = calc.margin_percent == null ? "—" : pct(calc.margin_percent);
  const targetRoi = calc.target_roi ?? 30;

  const scenarioText =
    fulfillment === "wfs"
      ? "Estimated WFS scenario — not a claim about the Featured Offer"
      : fulfillment === "seller"
        ? "You store and ship each order — modeled shipping is not an official Walmart fee"
        : fulfillment === "retail"
          ? "Walmart Retail scenario (planning only)"
          : "Fulfillment scenario unknown";

  const totalProfit = calc.total_net_profit ?? calc.net_profit;
  const totalProfitLabel = totalProfit == null
    ? "—"
    : `${totalProfit < 0 ? "−" : ""}${usd(Math.abs(totalProfit))}`;

  const copySummary = async () => {
    const lines = [
      `Sale price: ${usd(calc.sale_price)}`,
      `Cost price: ${usd(input.acquisition_cost)}`,
      `Max cost: ${usd(calc.maximum_acquisition_cost)}`,
      `Fulfillment scenario: ${fulfillment === "wfs" ? "WFS" : fulfillment === "seller" ? "Seller Fulfilled" : fulfillment}`,
      `Net profit / unit: ${usd(calc.net_profit)}`,
      `Net profit total: ${totalProfitLabel}`,
      `ROI: ${roiText}`,
      `Margin: ${marginText}`,
    ].join("\n");
    try {
      await navigator.clipboard?.writeText(lines);
      setCopied(true);
      toast.success("Profit summary copied");
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Could not copy");
    }
  };

  const breakdownRows = (calc.breakdown || []).filter(
    (b) => !(hideZero && b.kind === "expense" && (b.amount === 0 || b.amount == null)),
  );

  const relabel = (b) => {
    if (b.label === "WFS fulfillment fee") {
      return fulfillment === "wfs" ? "Estimated WFS fee" : "WFS fee";
    }
    if (b.label === "Inbound shipping") {
      return fulfillment === "seller" ? "Modeled shipping cost" : "Inbound shipping";
    }
    if (b.label === "Acquisition cost") return "Cost price";
    if (b.label === "Referral fee") {
      const rate = Math.round((input.referral_rate ?? 0.15) * 100);
      return `Referral fee (${rate}%)`;
    }
    return b.label;
  };

  return (
    <div className="ez-pc" data-testid="calculator">
      <header className="ez-pc-head">
        <h2 className="ez-pc-title">Profit calculator</h2>
        <div className="ez-pc-mode" role="group" aria-label="Calculator mode">
          <button
            type="button"
            data-testid="mode-beginner"
            aria-pressed={!advanced}
            className={cn(!advanced && "on")}
            onClick={() => setAdvanced(false)}
          >
            Beginner
          </button>
          <button
            type="button"
            data-testid="mode-advanced"
            aria-pressed={advanced}
            className={cn(advanced && "on")}
            onClick={() => setAdvanced(true)}
          >
            Advanced
          </button>
        </div>
      </header>

      <section className="ez-pc-metrics" data-testid="profit-summary">
        <article className={cn("ez-pc-metric", netTone === "danger" && "warn")} data-testid="result-netprofit">
          <div className="ez-pc-ml">
            {calc.net_profit == null ? "Net profit / unit" : calc.net_profit < 0 ? "Loss / unit" : "Net profit / unit"}
          </div>
          <div className="ez-pc-mv">{usd(calc.net_profit)}</div>
          <div className="ez-pc-mc">{calc.net_profit == null ? "Enter cost to compute" : "Per unit"}</div>
        </article>
        <article className="ez-pc-metric" data-testid="result-roi">
          <div className="ez-pc-ml">ROI</div>
          <div className="ez-pc-mv">{roiText}</div>
          <div className="ez-pc-mc">{calc.roi_percent == null ? "Enter cost" : "Return on cost price"}</div>
        </article>
        <article className="ez-pc-metric" data-testid="result-margin">
          <div className="ez-pc-ml">Profit margin</div>
          <div className="ez-pc-mv">{marginText}</div>
          <div className="ez-pc-mc">Profit as % of sale</div>
        </article>
        <article className="ez-pc-metric blue" data-testid="result-maxcost">
          <div className="ez-pc-ml">Max cost</div>
          <div className="ez-pc-mv">{usd(calc.maximum_acquisition_cost)}</div>
          <div className="ez-pc-mc">At {targetRoi}% target ROI</div>
        </article>
      </section>

      <h3 className="ez-pc-section">Core inputs</h3>
      <section className="ez-pc-grid">
        <div className="ez-pc-area-max">
          <MoneyField
            label="Max cost"
            value={calc.maximum_acquisition_cost}
            readOnly
            testid="result-maxcost-field"
            hint={`At ${targetRoi}% target ROI`}
            onReadOnlyClick={() => {
              if (calc.maximum_acquisition_cost != null) {
                setField("acquisition_cost", calc.maximum_acquisition_cost);
              }
            }}
          />
          <button
            type="button"
            data-testid="calc-use-maxcost"
            className="ez-pc-link"
            onClick={() => setField("acquisition_cost", calc.maximum_acquisition_cost)}
          >
            Use as cost price
          </button>
        </div>
        <div className="ez-pc-area-cost">
          <MoneyField
            label="Cost price"
            value={input.acquisition_cost}
            onChange={(v) => setField("acquisition_cost", v === "" || v === null ? null : v)}
            testid="input-acquisition"
            hint={
              input.acquisition_cost_provenance === "price_index"
                ? `From price index${input.acquisition_cost_source ? ` · ${input.acquisition_cost_source}` : ""}`
                : input.acquisition_cost_provenance === "missing"
                  ? "Not in price index — enter a cost"
                  : "Your purchase cost per unit"
            }
          />
        </div>
        <div className="ez-pc-area-qty">
          <QtyField
            value={input.quantity}
            onChange={(v) => setField("quantity", v)}
            testid="input-quantity"
            hint="Units in this calculation"
          />
        </div>
        <div className="ez-pc-area-sale">
          <MoneyField
            label="Sale price"
            value={input.sale_price}
            onChange={(v) => setField("sale_price", v === "" || v === null ? null : v)}
            testid="input-sale-price"
            hint="Current Walmart offer"
          />
          <button
            type="button"
            data-testid="calc-use-offer"
            className="ez-pc-link"
            onClick={() => setField("sale_price", product?.walmart?.price)}
          >
            Use offer price
          </button>
        </div>
      </section>

      <section className="ez-pc-fulfill">
        <div className="ez-pc-row">
          <span className="ez-pc-section" style={{ margin: 0 }}>Fulfillment scenario</span>
          <div className="ez-pc-toggle" role="group" aria-label="Fulfillment scenario">
            <button
              type="button"
              data-testid="calc-fulfillment-wfs"
              className={cn(fulfillment === "wfs" && "on")}
              onClick={() => onFulfillmentChange("wfs")}
            >
              WFS
            </button>
            <button
              type="button"
              data-testid="calc-fulfillment-seller"
              className={cn(fulfillment === "seller" && "on")}
              onClick={() => onFulfillmentChange("seller")}
            >
              Seller Fulfilled
            </button>
          </div>
        </div>
        {/* Keep select for a11y / legacy testid while primary UX is the toggle */}
        <select
          data-testid="calc-fulfillment"
          className="sr-only"
          value={fulfillment}
          aria-label="Fulfillment scenario"
          onChange={(e) => onFulfillmentChange(e.target.value)}
        >
          <option value="wfs">WFS</option>
          <option value="seller">Seller Fulfilled</option>
          <option value="retail">Walmart Retail</option>
          <option value="unknown">Unknown</option>
        </select>
        <p className="ez-pc-scenario" data-testid="calc-fulfillment-scenario">{scenarioText}</p>
        {product?.fulfillment_classification?.label && (
          <p className="ez-pc-scenario">
            Current listing: {product.fulfillment_classification.label}
          </p>
        )}
      </section>

      {advanced && (
        <div className="ez-pc-advanced" data-testid="advanced-fields">
          <h3 className="ez-pc-section">Advanced costs</h3>
          <div className="ez-pc-adv-grid">
            <MoneyField
              label="WFS fee"
              value={input.wfs_fee}
              onChange={(v) => setField("wfs_fee", v ?? 0)}
              testid="input-wfs-fee"
              hint="Published WFS schedule estimate when known"
            />
            <MoneyField
              label="Referral %"
              value={Math.round((input.referral_rate ?? 0) * 100)}
              onChange={(v) => setField("referral_rate", Math.min(100, Math.max(0, v ?? 0)) / 100)}
              testid="input-referral"
              prefix=""
              step={1}
              hint="Published referral schedule"
            />
            <MoneyField
              label="Inbound ship"
              value={input.inbound_shipping}
              onChange={(v) => setField("inbound_shipping", v ?? 0)}
              testid="input-inbound"
              hint="Modeled logistics — not an official Walmart fee"
            />
            <MoneyField
              label="Prep & label"
              value={input.prep_label}
              onChange={(v) => setField("prep_label", v ?? 0)}
              testid="input-prep"
            />
            <MoneyField
              label="Storage"
              value={input.storage}
              onChange={(v) => setField("storage", v ?? 0)}
              testid="input-storage"
              hint="Modeled storage cost"
            />
            <MoneyField
              label="Misc"
              value={input.misc}
              onChange={(v) => setField("misc", v ?? 0)}
              testid="input-misc"
            />
          </div>
          <div className="ez-pc-other-scenarios">
            <span className="ez-pc-help">Other planning scenarios</span>
            <div className="ez-pc-mode" style={{ gridTemplateColumns: "1fr 1fr", width: "100%", marginTop: 6 }}>
              <button type="button" className={cn(fulfillment === "retail" && "on")} onClick={() => onFulfillmentChange("retail")}>Walmart Retail</button>
              <button type="button" className={cn(fulfillment === "unknown" && "on")} onClick={() => onFulfillmentChange("unknown")}>Unknown</button>
            </div>
          </div>
        </div>
      )}

      <div className="ez-pc-divider" />

      <section className={cn("ez-pc-accordion", breakdownOpen && "open")} data-testid="profit-breakdown-accordion">
        <button
          type="button"
          className="ez-pc-accordion-trigger"
          id="profitToggle"
          aria-expanded={breakdownOpen}
          aria-controls="profitDetails"
          data-testid="profit-breakdown-toggle"
          onClick={() => setBreakdownOpen((o) => !o)}
        >
          <span className="ez-pc-accordion-title">Profit breakdown</span>
          <span className="ez-pc-accordion-total">
            <span data-testid="profit-breakdown-summary">{totalProfitLabel}</span>
            <i className="ez-pc-accordion-chevron" aria-hidden />
          </span>
        </button>
        <div className="ez-pc-accordion-region" id="profitDetails" role="region" aria-labelledby="profitToggle">
          <div className="ez-pc-accordion-inner">
            <div className="ez-pc-accordion-content">
              <div className="ez-pc-break-toolbar">
                <button
                  type="button"
                  data-testid="calc-hide-zero"
                  className={cn("ez-pc-chip", hideZero && "on")}
                  onClick={() => setHideZero((z) => !z)}
                >
                  Hide $0
                </button>
              </div>
              <div className="ez-pc-break" data-testid="profit-breakdown">
                {breakdownRows.map((b, i) => {
                  const isTotal = b.kind === "total";
                  const label = relabel(b);
                  const amount = b.amount;
                  let display = "—";
                  if (amount != null) {
                    if (isTotal) {
                      display = `${amount < 0 ? "−" : ""}${usd(Math.abs(amount))}`;
                    } else if (b.kind === "expense") {
                      display = moneyNeg(amount);
                    } else {
                      display = usd(amount);
                    }
                  }
                  return (
                    <div
                      key={`${b.label}-${i}`}
                      className={cn("ez-pc-line", b.kind === "expense" && "fee", isTotal && "total")}
                    >
                      <span>{isTotal ? `= ${label}` : label}</span>
                      <b>{display}</b>
                    </div>
                  );
                })}
              </div>
              <section className="ez-pc-summary">
                <div>
                  <span>Break-even price</span>
                  <b>{usd(calc.break_even_price)}</b>
                </div>
                <div>
                  <span>Total fees</span>
                  <b>{usd(calc.total_walmart_fees)}</b>
                </div>
                <div>
                  <span>Landed cost</span>
                  <b>{usd(calc.landed_cost)}</b>
                </div>
                <div>
                  <span>Walmart payout</span>
                  <b>{usd(calc.estimated_walmart_payout)}</b>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      <div className="ez-pc-actions">
        <button type="button" data-testid="calc-reset" onClick={onReset}>Reset</button>
        <button type="button" data-testid="calc-copy" onClick={copySummary}>
          {copied ? "Copied" : "Copy summary"}
        </button>
        <span
          className="ez-pc-formula"
          tabIndex={0}
          title={`Formula ${calc.formula_version}`}
          aria-label={`Formula ${calc.formula_version}`}
        >
          ⓘ
        </span>
      </div>
    </div>
  );
}

export default ProfitCalculator;
